import { useEffect, useMemo, useState } from 'react';

// Toastify
import { toast } from 'react-toastify';

// Utilities
import { cleanString, isQueryableFieldType } from '../apiUtils';
import {
  isComposerEnumMultiOperator,
  normalizeComposerOperatorForFieldType,
  resolveComposerFieldType,
} from './apiComposerFilters';
import { createComposerRequest } from './apiComposerUtils';

export default function useApiComposer({
  workspaceId,
  schemaTypes,
  baseUrl,
  runTestRequest,
}) {

  // ----- STATE ----- //

  const [composerIntent, setComposerIntent] = useState('list_nodes');
  const [composerTypeId, setComposerTypeId] = useState('');
  const [composerNodeId, setComposerNodeId] = useState('');
  const [composerLimit, setComposerLimit] = useState('25');
  const [composerFieldId, setComposerFieldId] = useState('');
  const [composerOperator, setComposerOperator] = useState('eq');
  const [composerPredicateValue, setComposerPredicateValue] = useState('');
  const [composerPredicateJoin, setComposerPredicateJoin] = useState('and');
  const [composerExtraQueryFilters, setComposerExtraQueryFilters] = useState([]);
  const [composerQuerySorts, setComposerQuerySorts] = useState([]);
  const [composerWriteTitle, setComposerWriteTitle] = useState('');
  const [composerWriteValues, setComposerWriteValues] = useState('{}');
  const [composerBusy, setComposerBusy] = useState(false);
  const [composerError, setComposerError] = useState('');
  const [composerResult, setComposerResult] = useState(null);

  // ----- MEMOIZED VALUES ----- //

  const selectedType = useMemo(() => {
    if (!composerTypeId) return null;
    return schemaTypes.find((row) => row.typeId === composerTypeId) || null;
  }, [composerTypeId, schemaTypes]);

  const selectedFields = useMemo(() => {
    if (!selectedType) return [];
    const rows = Array.isArray(selectedType.fields) ? selectedType.fields : [];
    return rows.filter((fieldRow) => isQueryableFieldType(fieldRow?.type));
  }, [selectedType]);

  const selectedField = useMemo(() => {
    if (!composerFieldId) return null;
    return selectedFields.find((row) => row.fieldId === composerFieldId) || null;
  }, [composerFieldId, selectedFields]);

  const selectedFieldOptionValues = useMemo(() => {
    const options = Array.isArray(selectedField?.options) ? selectedField.options : [];
    return options
      .map((optionRow) => cleanString(optionRow?.value || optionRow))
      .filter(Boolean);
  }, [selectedField?.options]);

  const selectedFieldMap = useMemo(() => {
    const map = new Map();
    selectedFields.forEach((fieldRow) => {
      if (!fieldRow?.fieldId) return;
      map.set(fieldRow.fieldId, fieldRow);
    });
    return map;
  }, [selectedFields]);

  // ----- EFFECTS ----- // 

  useEffect(() => {
    if (schemaTypes.length === 0) {
      if (composerTypeId) setComposerTypeId('');
      return;
    }

    const exists = schemaTypes.some((row) => row.typeId === composerTypeId);
    if (!exists) {
      setComposerTypeId(schemaTypes[0].typeId);
    }
  }, [composerTypeId, schemaTypes]);

  useEffect(() => {
    if (selectedFields.length === 0) {
      if (composerFieldId) setComposerFieldId('');
      return;
    }

    const exists = selectedFields.some((row) => row.fieldId === composerFieldId);
    if (!exists) {
      setComposerFieldId(selectedFields[0].fieldId);
    }
  }, [composerFieldId, selectedFields]);

  useEffect(() => {
    const allowedFieldIds = new Set(selectedFields.map((row) => row.fieldId));
    const selectedFieldById = new Map(
      selectedFields.map((row) => [cleanString(row?.fieldId), row]),
    );

    setComposerExtraQueryFilters((prev) => {
      let changed = false;

      const next = prev
        .filter((row) => {
          const keep = allowedFieldIds.has(cleanString(row?.fieldId));
          if (!keep) changed = true;
          return keep;
        })
        .map((row) => {
          const fieldId = cleanString(row?.fieldId);
          const fieldDef = selectedFieldById.get(fieldId) || null;
          const fieldType = resolveComposerFieldType(fieldDef, row?.fieldType || 'string');
          const normalizedOperator = normalizeComposerOperatorForFieldType(fieldType, row?.operator || 'eq');
          const currentOperator = cleanString(row?.operator).toLowerCase();

          if (currentOperator !== normalizedOperator || cleanString(row?.fieldType).toLowerCase() !== fieldType) {
            changed = true;
            return {
              ...row,
              fieldType,
              operator: normalizedOperator,
            };
          }

          return row;
        });

      return changed ? next : prev;
    });

    setComposerQuerySorts((prev) => {
      const next = prev.filter((row) => allowedFieldIds.has(cleanString(row?.fieldId)));
      return next.length === prev.length ? prev : next;
    });
  }, [selectedFields]);

  useEffect(() => {
    if (composerIntent !== 'query_nodes') return;

    const primaryFieldType = resolveComposerFieldType(selectedField, 'string');
    const normalizedOperator = normalizeComposerOperatorForFieldType(primaryFieldType, composerOperator);
    if (normalizedOperator !== cleanString(composerOperator).toLowerCase()) {
      setComposerOperator(normalizedOperator);
    }
  }, [composerIntent, composerOperator, selectedField]);

  useEffect(() => {
    if (composerIntent !== 'query_nodes') return;

    const selectedType = cleanString(selectedField?.type).toLowerCase();
    const selectedOperator = cleanString(composerOperator).toLowerCase();
    const usesBooleanSelectablePredicate = ['boolean', 'bool', 'checkbox'].includes(selectedType);
    const usesEnumSelectablePredicate = (selectedType === 'enum' || selectedType === 'multiselect' || selectedType === 'tags') && selectedFieldOptionValues.length > 0;
    const usesSelectablePredicate = usesBooleanSelectablePredicate || usesEnumSelectablePredicate;
    if (!usesSelectablePredicate) return;

    const allowedValues = usesBooleanSelectablePredicate
      ? ['true', 'false']
      : selectedFieldOptionValues;

    if (usesEnumSelectablePredicate && isComposerEnumMultiOperator(selectedOperator)) {
      const currentValues = Array.isArray(composerPredicateValue)
        ? composerPredicateValue.map((value) => cleanString(value)).filter(Boolean)
        : (() => {
          const singleValue = cleanString(composerPredicateValue);
          return singleValue ? [singleValue] : [];
        })();

      const normalizedValues = currentValues.filter((value) => allowedValues.includes(value));
      const valuesChanged = normalizedValues.length !== currentValues.length
        || normalizedValues.some((value, index) => value !== currentValues[index]);

      if (valuesChanged || !Array.isArray(composerPredicateValue)) {
        setComposerPredicateValue(normalizedValues);
      }
      return;
    }

    const normalizedValue = cleanString(composerPredicateValue);
    if (!normalizedValue) return;
    if (allowedValues.includes(normalizedValue)) return;

    setComposerPredicateValue('');
  }, [
    composerIntent,
    composerOperator,
    composerPredicateValue,
    selectedField?.type,
    selectedFieldOptionValues,
  ]);

  const composerQueryFilters = useMemo(() => {
    const primaryFieldType = selectedFieldMap.get(composerFieldId)?.type || selectedField?.type;
    const primaryFilter = {
      fieldId: composerFieldId,
      operator: composerOperator,
      value: composerPredicateValue,
      joinToNext: composerPredicateJoin,
      fieldType: primaryFieldType,
    };

    const extraFilters = composerExtraQueryFilters.map((row) => {
      const fieldId = cleanString(row?.fieldId);
      const fieldType = selectedFieldMap.get(fieldId)?.type || row?.fieldType;
      return {
        ...row,
        fieldId,
        fieldType,
      };
    });

    return [primaryFilter, ...extraFilters];
  }, [
    composerExtraQueryFilters,
    composerFieldId,
    composerOperator,
    composerPredicateJoin,
    composerPredicateValue,
    selectedField?.type,
    selectedFieldMap,
  ]);

  const composerQuerySortRows = useMemo(() => {
    return composerQuerySorts.map((row) => ({
      ...row,
      fieldId: cleanString(row?.fieldId),
      direction: cleanString(row?.direction || 'asc').toLowerCase() === 'desc' ? 'desc' : 'asc',
    }));
  }, [composerQuerySorts]);

  const workspacePlaceholder = workspaceId || '<workspace-id>';
  const base = cleanString(baseUrl) || '<api-base-url>';

  const composerRequest = useMemo(() => createComposerRequest({
    intent: composerIntent,
    typeId: composerTypeId,
    nodeId: composerNodeId,
    limit: composerLimit,
    fieldId: composerFieldId,
    fieldType: selectedField?.type,
    operator: composerOperator,
    predicateValue: composerPredicateValue,
    queryFilters: composerIntent === 'query_nodes' ? composerQueryFilters : [],
    querySorts: composerIntent === 'query_nodes' ? composerQuerySortRows : [],
    writeTitle: composerWriteTitle,
    writeValuesText: composerWriteValues,
  }), [
    composerQueryFilters,
    composerQuerySortRows,
    composerFieldId,
    composerIntent,
    composerLimit,
    composerNodeId,
    composerOperator,
    composerPredicateValue,
    composerTypeId,
    composerWriteTitle,
    composerWriteValues,
    selectedField?.type,
  ]);

  const composerPreview = useMemo(() => {
    if (!composerRequest || composerRequest.error) return '';

    return JSON.stringify({
      label: composerRequest.label,
      method: composerRequest.method,
      path: composerRequest.path,
      body: composerRequest.body,
      executionPolicy: {
        canTestRun: composerRequest.canTestRun,
        mode: composerRequest.canTestRun ? 'read_only' : 'generate_only',
      },
    }, null, 2);
  }, [composerRequest]);

  const composerCurlSnippet = useMemo(() => {
    if (!composerRequest || composerRequest.error) return '';

    const lines = [
      `curl -sS -X ${composerRequest.method}`,
      '  -H "Authorization: Bearer <bearer-token>"',
    ];

    if (composerRequest.method === 'PATCH' || composerRequest.method === 'DELETE') {
      lines.push('  -H "If-Match: <etag-from-get-response>"');
    }

    if (composerRequest.body !== undefined) {
      lines.push('  -H "Content-Type: application/json"');
      lines.push(`  --data-raw '${JSON.stringify(composerRequest.body)}'`);
    }

    lines.push(`  "${base}/v1/workspaces/${workspacePlaceholder}${composerRequest.path}"`);

    return lines.join(' \\\n');
  }, [base, composerRequest, workspacePlaceholder]);

  const composerFetchSnippet = useMemo(() => {
    if (!composerRequest || composerRequest.error) return '';

    const lines = [
      `const baseUrl = '${base}';`,
      `const workspaceId = '${workspacePlaceholder}';`,
      "const token = '<bearer-token>';",
      `const endpointPath = '${composerRequest.path}';`,
      '',
      "const headers = { Authorization: 'Bearer ' + token };",
    ];

    if (composerRequest.method === 'PATCH' || composerRequest.method === 'DELETE') {
      lines.push("headers['If-Match'] = '<etag-from-get-response>'; ");
    }

    if (composerRequest.body !== undefined) {
      lines.push("headers['Content-Type'] = 'application/json';");
    }

    lines.push('');
    lines.push("const response = await fetch(baseUrl + '/v1/workspaces/' + workspaceId + endpointPath, {");
    lines.push(`  method: '${composerRequest.method}',`);
    lines.push('  headers,');
    if (composerRequest.body !== undefined) {
      lines.push(`  body: JSON.stringify(${JSON.stringify(composerRequest.body, null, 2)}),`);
    }
    lines.push('});');
    lines.push("if (!response.ok) throw new Error('Request failed (' + response.status + ')');");
    lines.push('const payload = await response.json();');
    lines.push('console.log(payload);');

    return lines.join('\n');
  }, [base, composerRequest, workspacePlaceholder]);

  useEffect(() => {
    setComposerError('');
    setComposerResult(null);
  }, [composerPreview]);

  const handleCopy = async (value, successMessage) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(successMessage);
    } catch (copyError) {
      toast.error(copyError?.message || 'Could not copy text.');
    }
  };

  const handleComposerTestRun = async () => {
    if (!workspaceId) {
      setComposerError('Select an active workspace before running a Composer request.');
      return;
    }

    if (!composerRequest || composerRequest.error) {
      setComposerError(composerRequest?.error || 'Composer request is invalid.');
      return;
    }

    if (!composerRequest.canTestRun) {
      setComposerError('Composer test run is read-only in v1 preview. This request is generate-only.');
      return;
    }

    if (typeof runTestRequest !== 'function') {
      setComposerError('Composer test request service is not available.');
      return;
    }

    setComposerBusy(true);
    setComposerError('');
    setComposerResult(null);

    try {
      const response = await runTestRequest({
        workspaceId,
        path: composerRequest.path,
        method: composerRequest.method,
        body: composerRequest.body,
      });

      setComposerResult(response);
      toast.success(`Composer test request succeeded (${response?.status || 200}).`);
    } catch (runError) {
      setComposerError(runError?.message || 'Composer test request failed.');
      toast.error(runError?.message || 'Composer test request failed.');
    } finally {
      setComposerBusy(false);
    }
  };

  return {
    composerIntent,
    composerTypeId,
    composerNodeId,
    composerLimit,
    composerFieldId,
    composerOperator,
    composerPredicateValue,
    composerPredicateJoin,
    composerExtraQueryFilters,
    composerQuerySorts,
    composerWriteTitle,
    composerWriteValues,
    composerBusy,
    composerError,
    composerResult,
    selectedField,
    selectedFields,
    composerRequest,
    composerPreview,
    composerCurlSnippet,
    composerFetchSnippet,
    setComposerIntent,
    setComposerTypeId,
    setComposerNodeId,
    setComposerLimit,
    setComposerFieldId,
    setComposerOperator,
    setComposerPredicateValue,
    setComposerPredicateJoin,
    setComposerExtraQueryFilters,
    setComposerQuerySorts,
    setComposerWriteTitle,
    setComposerWriteValues,
    handleCopy,
    handleComposerTestRun,
  };
}
